import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAdminAuth } from '../../hooks/useAdminAuth'
import { ROUTES } from '../../config/constants'
import { adminService } from '../../services/adminService'
import Button from '../ui/Button'

export default function AdminLayout({ children }) {
  const { admin, logout } = useAdminAuth()
  const location = useLocation()
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)

  const handleLogout = () => {
    logout()
  }

  const isActive = (path) => {
    return location.pathname === path
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()
    setPasswordError('')
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters')
      return
    }
    setPasswordLoading(true)
    try {
      const res = await adminService.changePassword(currentPassword, newPassword)
      if (res.success) {
        setPasswordSuccess(true)
        setCurrentPassword('')
        setNewPassword('')
        setTimeout(() => {
          setShowPasswordModal(false)
          setPasswordSuccess(false)
        }, 1500)
      } else {
        setPasswordError(res.error || 'Failed to update password')
      }
    } catch (err) {
      setPasswordError(err.message || 'Failed to update password')
    } finally {
      setPasswordLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-stadium-black">
      {/* Admin Navbar */}
      <nav className="bg-stadium-black/95 backdrop-blur-md border-b border-pitch-green/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-4 md:gap-8">
              <Link to={ROUTES.ADMIN_DASHBOARD} className="text-lg md:text-xl font-bold text-pitch-green text-glow whitespace-nowrap">
                REEL CRICKET ADMIN
              </Link>
              <div className="hidden md:flex items-center gap-4">
                <Link
                  to={ROUTES.ADMIN_DASHBOARD}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    isActive(ROUTES.ADMIN_DASHBOARD)
                      ? 'bg-pitch-green/20 text-pitch-green'
                      : 'text-gray-400 hover:text-pitch-green'
                  }`}
                >
                  Dashboard
                </Link>
                <Link
                  to={ROUTES.ADMIN_SUBMISSIONS}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    isActive(ROUTES.ADMIN_SUBMISSIONS)
                      ? 'bg-pitch-green/20 text-pitch-green'
                      : 'text-gray-400 hover:text-pitch-green'
                  }`}
                >
                  Submissions
                </Link>
                <Link
                  to={ROUTES.ADMIN_ORDERS}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    isActive(ROUTES.ADMIN_ORDERS)
                      ? 'bg-pitch-green/20 text-pitch-green'
                      : 'text-gray-400 hover:text-pitch-green'
                  }`}
                >
                  Orders
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <span className="text-gray-400 text-sm hidden md:block">
                {admin?.username}
              </span>
              <button
                type="button"
                onClick={() => setShowPasswordModal(true)}
                className="text-gray-400 hover:text-pitch-green text-sm px-2 py-1"
              >
                Change password
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="bg-red-500/20 hover:bg-red-500/30 text-red-400 px-3 py-2 md:px-4 rounded-lg transition-colors text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Change password modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => !passwordLoading && setShowPasswordModal(false)}>
          <div className="bg-gray-900 border-2 border-pitch-green/30 rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-4">Change password</h3>
            {passwordSuccess ? (
              <p className="text-pitch-green">Password updated successfully.</p>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-4">
                {passwordError && <p className="text-red-400 text-sm">{passwordError}</p>}
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Current password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="w-full bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-pitch-green"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">New password (min 8 characters)</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-pitch-green"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={passwordLoading} loading={passwordLoading}>Update</Button>
                  <Button type="button" variant="outline" onClick={() => setShowPasswordModal(false)} disabled={passwordLoading}>Cancel</Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {children}
      </main>
    </div>
  )
}
